import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as PreviewIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';

/**
 * Storefront Export Page
 *
 * Export products from management platform to sivad-storefront format
 */
const StorefrontExportPage = () => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedLooks, setSelectedLooks] = useState([]);
  const [format, setFormat] = useState('javascript');
  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');

  // Fetch preview on mount
  useEffect(() => {
    fetchPreview();
  }, []);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/storefront-export/preview');
      const data = await response.json();

      if (data.success) {
        setPreview(data.preview);

        // Set campaigns for selection
        if (data.preview.campaigns) {
          setCampaigns(data.preview.campaigns);
        }

        // Set brand for look selection
        if (data.preview.brand) {
          setBrands([data.preview.brand]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch preview:', error);
      toast.error('Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCampaigns = async () => {
    try {
      setLoading(true);
      toast.loading('Generating export...', { id: 'export' });

      const params = new URLSearchParams({
        format,
        limit: 10,
        status: 'all'
      });

      const response = await fetch(`/api/storefront-export/campaigns?${params}`);

      if (format === 'javascript') {
        // Download file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'storefront-products.js';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success('File downloaded!', { id: 'export' });
      } else {
        const data = await response.json();
        console.log('Exported campaigns:', data.campaigns);

        // Download JSON
        const blob = new Blob([JSON.stringify(data.campaigns, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'storefront-campaigns.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success(`Exported ${data.count.campaigns} campaign(s) with ${data.count.products} products!`, { id: 'export' });
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed', { id: 'export' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportBrandLooks = async () => {
    if (!selectedBrand) {
      toast.error('Please select a brand');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Generating export...', { id: 'export' });

      const params = new URLSearchParams({
        format,
        limit: 10
      });

      const response = await fetch(`/api/storefront-export/brand/${selectedBrand}/looks?${params}`);

      if (format === 'javascript') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'brand-looks.js';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success('File downloaded!', { id: 'export' });
      } else {
        const data = await response.json();

        const blob = new Blob([JSON.stringify(data.products, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'brand-looks.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success(`Exported ${data.count} looks!`, { id: 'export' });
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed', { id: 'export' });
    } finally {
      setLoading(false);
    }
  };

  const handleCustomExport = async () => {
    if (selectedCampaigns.length === 0 && selectedLooks.length === 0) {
      toast.error('Please select at least one campaign or look');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Generating custom export...', { id: 'export' });

      const response = await fetch('/api/storefront-export/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignIds: selectedCampaigns,
          brandId: selectedBrand,
          lookIds: selectedLooks,
          format,
          filename: 'custom-storefront-products'
        })
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'javascript' ? 'js' : 'json';
      a.download = `custom-storefront-products.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Custom export downloaded!', { id: 'export' });
    } catch (error) {
      console.error('Custom export failed:', error);
      toast.error('Custom export failed', { id: 'export' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <StoreIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Storefront Export
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Export products to sivad-storefront format
          </Typography>
        </Box>
        <Tooltip title="Refresh Preview">
          <IconButton onClick={fetchPreview} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>About Storefront Export:</strong> This tool converts your campaigns and looks from the
          management platform into the format required by the sivad-storefront 3D boutique. The exported
          file includes product details, tier pricing, images, and 3D model references.
        </Typography>
      </Alert>

      {/* Preview Stats */}
      {preview && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Available Campaigns
                </Typography>
                <Typography variant="h3" color="primary">
                  {preview.campaigns?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active and scheduled campaigns
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Available Looks
                </Typography>
                <Typography variant="h3" color="primary">
                  {preview.brand?.lookCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  From {preview.brand?.name || 'brand'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Export Options */}
      <Grid container spacing={3}>
        {/* Quick Export - All Campaigns */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Export All Campaigns
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Export all active and scheduled campaigns to storefront format
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Output Format</InputLabel>
              <Select value={format} onChange={(e) => setFormat(e.target.value)} label="Output Format">
                <MenuItem value="javascript">JavaScript (.js)</MenuItem>
                <MenuItem value="json">JSON (.json)</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              fullWidth
              startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleExportCampaigns}
              disabled={loading || !preview?.campaigns?.length}
            >
              Export All Campaigns
            </Button>

            {preview?.campaigns && preview.campaigns.length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Will export: {preview.campaigns.slice(0, 3).map(c => c.name).join(', ')}
                  {preview.campaigns.length > 3 && ` +${preview.campaigns.length - 3} more`}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Quick Export - Brand Looks */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Export Brand Looks
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Export looks from a specific brand to storefront format
            </Typography>

            {preview?.brand && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={preview.brand.name}
                  color="primary"
                  onDelete={() => setSelectedBrand('')}
                  onClick={() => setSelectedBrand(preview.brand.id)}
                  sx={{ mb: 1 }}
                />
              </Box>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Output Format</InputLabel>
              <Select value={format} onChange={(e) => setFormat(e.target.value)} label="Output Format">
                <MenuItem value="javascript">JavaScript (.js)</MenuItem>
                <MenuItem value="json">JSON (.json)</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              fullWidth
              startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleExportBrandLooks}
              disabled={loading || !preview?.brand}
            >
              Export Brand Looks
            </Button>

            {preview?.brand?.looks && preview.brand.looks.length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Will export: {preview.brand.looks.slice(0, 3).map(l => l.name).join(', ')}
                  {preview.brand.looks.length > 3 && ` +${preview.brand.looks.length - 3} more`}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Custom Campaign Selection */}
      {preview?.campaigns && preview.campaigns.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Custom Export - Select Campaigns
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose specific campaigns to export
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Campaigns</InputLabel>
            <Select
              multiple
              value={selectedCampaigns}
              onChange={(e) => setSelectedCampaigns(e.target.value)}
              label="Select Campaigns"
              renderValue={(selected) =>
                `${selected.length} campaign(s) selected`
              }
            >
              {preview.campaigns.map((campaign) => (
                <MenuItem key={campaign.id} value={campaign.id}>
                  <Checkbox checked={selectedCampaigns.indexOf(campaign.id) > -1} />
                  <ListItemText
                    primary={campaign.name}
                    secondary={`${campaign.productCount || 0} products, Status: ${campaign.status}`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Output Format</InputLabel>
            <Select value={format} onChange={(e) => setFormat(e.target.value)} label="Output Format">
              <MenuItem value="javascript">JavaScript (.js)</MenuItem>
              <MenuItem value="json">JSON (.json)</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            fullWidth
            color="secondary"
            startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleCustomExport}
            disabled={loading || selectedCampaigns.length === 0}
          >
            Export Selected Campaigns ({selectedCampaigns.length})
          </Button>
        </Paper>
      )}

      <Divider sx={{ my: 3 }} />

      {/* How to Use */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          How to Use Exported Data
        </Typography>
        <Box component="ol" sx={{ pl: 2 }}>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Click one of the export buttons above to download your product data
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Open your <code>sivad-storefront</code> project
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Replace the MOCK_TIER_DATA in <code>src/components/ui/TierCardOverlay.tsx</code>
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Import and use your exported data instead of the mock data
          </Typography>
          <Typography component="li" variant="body2">
            Refresh your storefront to see your real products with tier pricing!
          </Typography>
        </Box>

        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Pro Tip:</strong> Export in JavaScript format for direct import into your Next.js storefront.
            Use JSON format if you need to process the data further or store it in a database.
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default StorefrontExportPage;
